import { merge } from 'merge-anything';
import { isValidElement, PropsWithChildren, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useConfig } from '@orionprotocol/api';
import { config, utils } from '@orionprotocol/sdk';
import { Button } from '@orionprotocol/ui';
import {
  chainSchema,
  envInfoSchema,
  environment,
  envs,
  invariant,
} from '@orionprotocol/common';

import { appSlice } from '../../../shared/redux/slices';
import { displayError } from '../../../shared/utils';

import ConfigChains from './uiconfigchains.json';
import styles from './EnvWrapper.module.css';

const combinedChains = merge(config.chains, ConfigChains);
const validatedChains = chainSchema.parse(combinedChains);

export function EnvWrapper({
  children,
}: PropsWithChildren<unknown>): JSX.Element | null {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(appSlice.actions.setEnvironments(envs));
    dispatch(appSlice.actions.setChains(validatedChains));
    dispatch(appSlice.actions.setEnvironment(environment));
  }, [dispatch]);

  const { status, data: envConfig, refetch } = useConfig();

  useEffect(() => {
    if (status === 'success') {
      const extraData = envInfoSchema.parse(envConfig);

      dispatch(
        appSlice.actions.addEnvironment({
          envLabel: 'custom',
          env: {
            analyticsAPI: extraData.analyticsAPI,
            referralAPI: extraData.referralAPI,
            frontageAPI: extraData.frontageAPI,
            defaultNetwork: extraData.defaultNetwork,
            networks: Object.entries(extraData.networks).reduce(
              (prev, [chainId, network]) => {
                invariant(
                  utils.isValidChainId(chainId),
                  `No chain id ${chainId}`,
                );
                return {
                  ...prev,
                  [chainId]: network,
                };
              },
              {},
            ),
            atomicSwapSourceNetworkDefault:
              extraData.atomicSwapSourceNetworkDefault,
            isKucoinStyled: extraData?.isKucoinStyled,
            disabledFeatures: extraData?.disabledFeatures,
          },
        }),
      );
      dispatch(appSlice.actions.setEnvironment('custom'));
      dispatch(appSlice.actions.setIsCustomConfig(true));
    }

    if (status === 'error') {
      dispatch(appSlice.actions.setIsCustomConfig(false));
      if (import.meta.env.NODE_ENV === 'development') {
        console.warn('/config.json not found');
      }
      displayError("Can't fetch configuration");
    }
  }, [dispatch, envConfig, status]);

  if (status === 'loading') {
    return null;
  }

  return status === 'success' && isValidElement(children) ? (
    children
  ) : (
    <div className={styles.envWrapper}>
      <div className={styles.description}>Configuration loading error</div>
      <Button type="primary" className={styles.button} onClick={refetch}>
        Try again
      </Button>
    </div>
  );
}
